<template>
  <div>
    <!-- Real estate search -->
    <div class="mx-auto max-w-[1200px]">
      <!-- gap-3 -->
      <form :action="this.$route('web.search')" method="GET">
        <div
          class="mx-auto flex w-full flex-col items-center rounded-full bg-white text-sm text-black-78 shadow-search md:flex-row"
        >
          <v-dropdown-sm class="flex-1">
            <template v-slot:trigger>
              <button
                type="button"
                class="flex w-full items-center justify-center rounded-full px-5 py-5 font-medium text-black-50 hover:bg-green-10 lg:px-5"
              >
                <div class="h-6 w-6">
                  <img
                    src="/assets/images/countries/si.svg"
                    v-if="currentCountry == 'Slovenija'"
                    alt=""
                    class="h-6 w-6 rounded-full"
                  />
                  <img
                    src="/assets/images/countries/hr.svg"
                    v-if="currentCountry == 'Hrvaška'"
                    alt=""
                    class="h-6 w-6 rounded-full"
                  />
                </div>
                <div>
                  <v-icon icon="chevron-down" class="ml-2" size="5"></v-icon>
                </div>
              </button>
            </template>
            <template v-slot:content>
              <div class="flex flex-col gap-3.5">
                <a href="/drzava/svn" class="hover:text-green"> Slovenija </a>
                <a href="/drzava/hrv" class="hover:text-green"> Hrvaška </a>
              </div>
            </template>
          </v-dropdown-sm>

          <div class="w-full md:w-1/5 lg:w-1/5">
            <div class="relative z-1 flex">
              <label
                class="absolute left-0 top-0 z-1 flex h-full w-full cursor-pointer items-center rounded-full px-5 py-5 font-medium text-black-50 hover:bg-green-10 lg:px-10"
                v-if="!hideRegionLabel && regionOption === null"
                @click="openRegion"
              >
                <div class="overflow-hidden text-ellipsis whitespace-nowrap">
                  <span class="text-black-78">Regija</span>
                </div>
              </label>
              <v-select
                class="w-full font-medium"
                :options="regions"
                placeholder=""
                v-model="regionOption"
                label="region"
                @open="onOpen"
                @search:focus="onFocus('region')"
                @search:blur="onBlur('region')"
                @option:selected="clearLocations"
                ref="region"
                id="region"
                :name="'region'"
                :clearable="true"
              >
                <template v-slot:no-options>
                  Za ta iskalni niz ni zadetkov.
                </template>
              </v-select>
              <input
                type="hidden"
                name="region"
                :value="regionOption == null ? '' : regionOption.region"
              />
              <div
                class="absolute right-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer"
              >
                <v-icon
                  icon="close"
                  size="5"
                  @click="regionOption = null"
                ></v-icon>
              </div>

              <!-- Error state -->
              <div
                v-if="errors.region"
                class="absolute left-5 top-full -z-10 whitespace-nowrap rounded-b-xl bg-salmon px-5 py-2.5 text-xs font-medium leading-none text-white lg:left-1/2 lg:-translate-x-1/2"
              >
                <div class="flex items-center">
                  <v-icon
                    icon="x-circle"
                    size="3.5"
                    class="mr-1.5 text-white"
                  ></v-icon>
                  Prosimo vnesite regijo.
                </div>
              </div>
            </div>
          </div>

          <div class="w-full md:w-1/5 lg:w-1/5">
            <div class="relative z-1 flex">
              <label
                class="absolute left-0 top-0 z-1 flex h-full w-full cursor-pointer items-center rounded-full px-5 py-5 font-medium text-black-50 hover:bg-green-10 lg:px-10"
                v-if="!hideLocationLabel && locationOption === null"
                @click="openLocation"
              >
                <div class="overflow-hidden text-ellipsis whitespace-nowrap">
                  <span class="text-black-78">Lokacija:</span>
                  mesto, ulica, poštna št.
                </div>
              </label>
              <v-select
                class="w-full font-medium"
                :options="locationsData"
                placeholder=""
                v-model="locationOption"
                label="custom_label"
                @open="onOpen"
                @search:focus="onFocus('location')"
                @search:blur="onBlur('location')"
                @search="fetchLocations"
                @option:selected="setRegion"
                ref="location"
                id="location"
                :name="'location'"
              >
                <template v-slot:no-options>
                  Za ta iskalni niz ni zadetkov.
                </template>
              </v-select>
              <input
                type="hidden"
                name="location"
                :value="locationOption == null ? '' : locationOption.value"
              />
              <div
                class="absolute right-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer"
              >
                <v-icon
                  icon="close"
                  size="5"
                  @click="locationOption = null"
                ></v-icon>
              </div>

              <!-- Error state -->
              <div
                v-if="errors.location && !errors.region"
                class="absolute left-5 top-full -z-10 whitespace-nowrap rounded-b-xl bg-salmon px-5 py-2.5 text-xs font-medium leading-none text-white lg:left-1/2 lg:-translate-x-1/2"
              >
                <div class="flex items-center">
                  <v-icon
                    icon="x-circle"
                    size="3.5"
                    class="mr-1.5 text-white"
                  ></v-icon>
                  Prosimo vnesite lokacijo.
                </div>
              </div>
            </div>
          </div>

          <div class="w-full md:w-1/5 lg:w-1/5">
            <v-main-search-dropdown
              :placeholder="rentOrBuyPlaceholder.label"
              :icon="rentOrBuyPlaceholder.icon"
              ref="rentOrBuyDropdown"
            >
              <template v-slot:options>
                <div class="grid grid-cols-2 gap-5">
                  <div
                    v-for="(option, index) in rentOrBuyOptions"
                    v-bind:key="'rent_or_buy' + index"
                  >
                    <v-form-radio
                      name="rent_or_buy"
                      :id="'rent_or_buy_' + index"
                      :label="option.label"
                      :value="option.value"
                      v-model="rentOrBuyValue"
                      :fixed-height="true"
                    >
                      <template v-slot:icon>
                        <v-icon
                          :icon="option.icon"
                          size="5"
                          class="mr-2"
                        ></v-icon>
                      </template>
                    </v-form-radio>
                  </div>
                </div>
              </template>
            </v-main-search-dropdown>
          </div>

          <div class="w-full md:w-1/5 lg:w-1/5">
            <div class="relative">
              <v-main-search-dropdown
                :placeholder="realestateTypePlaceholder.label"
                :icon="realestateTypePlaceholder.icon"
                ref="realestateTypeDropdown"
              >
                <template v-slot:options>
                  <div class="grid grid-cols-2 gap-x-5 gap-y-2.5">
                    <div
                      v-for="(option, index) in realestateTypeOptions"
                      v-bind:key="'realestate_property_type' + index"
                    >
                      <v-form-radio
                        name="realestate_property_type"
                        :id="'realestate_property_type_' + index"
                        :label="option.label"
                        :value="option.value"
                        v-model="realestateTypeValue"
                        :fixed-height="true"
                      >
                        <template v-slot:icon>
                          <v-icon
                            :icon="option.icon"
                            size="5"
                            class="mr-2 shrink-0"
                          ></v-icon>
                        </template>
                      </v-form-radio>
                    </div>
                  </div>
                </template>
              </v-main-search-dropdown>
              <!-- Error state -->
              <div
                v-if="errors.realestate_property_type"
                class="absolute left-5 top-full -z-10 whitespace-nowrap rounded-b-xl bg-salmon px-5 py-2.5 text-xs font-medium leading-none text-white lg:left-[55%] lg:-translate-x-1/2"
              >
                <div class="flex items-center">
                  <v-icon
                    icon="x-circle"
                    size="3.5"
                    class="mr-1.5 text-white"
                  ></v-icon>
                  Prosimo izberite tip nepremičnine.
                </div>
              </div>
            </div>
          </div>
          <div class="flex-1 pr-2.5">
            <button
              class="flex items-center justify-center gap-[10px] rounded-full bg-green px-[15px] py-3 text-body-medium-button text-white hover:bg-green-hover"
              type="submit"
            >
              <v-icon icon="loupe" size="5" class=""></v-icon>
              <span class="pointer-events-none">Iščite</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import MainSearchDropdown from "./MainSearchDropdown.vue";
import Icon from "../Common/Icon/Icon.vue";

export default {
  name: "MainSearchRealEstateDesktop",

  components: {
    "v-main-search-dropdown": MainSearchDropdown,
    Icon,
  },

  props: {
    i18n: {
      type: Object,
      required: true,
    },
    enums: {
      type: Object,
      required: true,
    },
    rentOrBuyOptions: {
      type: Object,
      required: true,
    },
    realestateTypeOptions: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
      default: {},
    },
    old: {
      type: Object,
      required: false,
      default: {},
    },
    currentCountry: {
      type: String,
      required: true,
      default: "",
    },
  },

  data() {
    return {
      locationOption: null,
      hideLocationLabel: false,
      rentOrBuyValue: "kauf",
      realestateTypeValue: "wohnung",
      minPrice: "",
      maxPrice: "",
      regions: [],
      regionOption: null,
      hideRegionLabel: false,
      locationsData: [],
    };
  },
  mounted() {
    if (this.old.location) {
      this.locationOption = this.locations.find(
        (option) => option.value == this.old.location
      );
    }

    this.rentOrBuyValue = this.old.rent_or_buy ?? "kauf";
    this.realestateTypeValue = this.old.realestate_property_type ?? "wohnung";
    this.minPrice = this.old.price_from?.replaceAll(".", "") ?? "";
    this.maxPrice = this.old.price_to?.replaceAll(".", "") ?? "";
  },

  // watch: {
  //   locationOption: function (newVal, oldVal) {
  //     console.log("locationOption");
  //     console.log(newVal);
  //     console.log(oldVal);
  //     if (newVal) {
  //       this.regionOption = {
  //         region: newVal.region,
  //       };

  //       console.log(this.regionOption);
  //     }
  //   },

  //   regionOption: function (newVal, oldVal) {
  //     console.log("regionOption");
  //     console.log(newVal);
  //     console.log(oldVal);
  //     if (newVal?.region !== oldVal?.region) {
  //       this.locationOption = null;
  //     }
  //   },
  // },

  computed: {
    rentOrBuyPlaceholder() {
      this.$refs.rentOrBuyDropdown
        ? (this.$refs.rentOrBuyDropdown.show = false)
        : null;

      return this.rentOrBuyOptions.find(
        (option) => option.value === this.rentOrBuyValue
      );
    },
    realestateTypePlaceholder() {
      this.$refs.realestateTypeDropdown
        ? (this.$refs.realestateTypeDropdown.show = false)
        : null;

      if (this.realestateTypeValue === "") {
        return {
          label: "Tip nepremičnine",
          icon: "house",
        };
      }

      return this.realestateTypeOptions.find(
        (option) => option.value === this.realestateTypeValue
      );
    },
    pricePlaceholder() {
      if (this.minPrice && !this.maxPrice) {
        return {
          label: `${this.$filters.formatNumber(this.minPrice)} €`,
          icon: "euro",
        };
      }

      if (!this.minPrice && this.maxPrice) {
        return {
          label: `0 - ${this.$filters.formatNumber(this.maxPrice)} €`,
          icon: "euro",
        };
      }

      if (this.minPrice && this.maxPrice) {
        return {
          label: `${this.$filters.formatNumber(
            this.minPrice
          )} € - ${this.$filters.formatNumber(this.maxPrice)} €`,
          icon: "euro",
        };
      }

      return {
        label: "Cena",
        icon: "euro",
      };
    },
    countryPlaceholder() {
      return {
        label: this.currentCountry,
      };
    },
  },
  methods: {
    onOpen() {},
    openLocation() {
      // Access the Vue Select instance using the ref
      const selectComponent = this.$refs.location;

      // Check if the Vue Select instance exists and is defined
      if (selectComponent) {
        selectComponent.$refs.search.focus();
      }
    },
    openRegion() {
      // Access the Vue Select instance using the ref
      const selectComponent = this.$refs.region;

      // Check if the Vue Select instance exists and is defined
      if (selectComponent) {
        selectComponent.$refs.search.focus();
      }
    },
    onFocus(value) {
      if (value === "region") {
        this.hideRegionLabel = true;
      } else {
        this.hideLocationLabel = true;
      }
    },
    onBlur(value) {
      if (value === "region") {
        this.hideRegionLabel = false;
      } else {
        this.hideLocationLabel = false;
      }
    },
    resetPrice() {
      this.minPrice = "";
      this.maxPrice = "";
    },
    confirmPrice() {
      this.$refs.priceDropdown.show = false;
    },
    clearLocations() {
      this.locationsData = [];
      this.locationOption = null;
    },
    setRegion() {
      this.regionOption = {
        region: this.locationOption.region,
      };
    },
    fetchRegions() {
      axios
        .get(this.$route("api.regions"))
        .then((response) => {
          const regions = response.data.data;
          this.regions = regions;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    // fetchLocationsFromRegion() {
    //   this.locationOption = null;
    //   this.locationsData = [];

    //   axios
    //     .get("/api/front/locations", {
    //       params: {
    //         region: this.regionOption?.region,
    //       },
    //     })
    //     .then((response) => {
    //       console.log(response.data.data);
    //       let locations = response.data.data;
    //       this.locationsData = locations.map((location) => {
    //         return {
    //           value: location["location"],
    //           custom_label: location["location"],
    //           region: location["region"],
    //         };
    //       });
    //     })
    //     .catch((error) => {
    //       console.log(error);
    //     });
    // },

    fetchLocations: debounce(async function (value) {
      if (value === "") {
        // this.locationsData = [];
        return;
      }

      try {
        const response = await axios.get(this.$route("api.locations"), {
          params: {
            region: this.regionOption?.region,
            search: value,
          },
        });
        let locations = response.data.data;
        this.locationsData = locations.map((location) => {
          return {
            value: location["code"],
            custom_label: location["location"],
            region: location["region"],
          };
        });
      } catch (error) {
        console.log(error);
      } finally {
        // Ensure loading(false) is defined
        // this.loading(false);
      }
    }, 500),
  },

  created() {
    this.fetchRegions();
  },
};
</script>
